import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Button,
  Dialog,
  DialogContent,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FeedIcon from '@mui/icons-material/Feed';
import { useHttp } from 'hooks/use-fetch';
import { useUnifiedAuth } from 'components/Common/UnifiedAuthContext';
import { bulkUploadUrls, providers } from 'utils/apiUrls';
import { useQuery } from 'react-query';
import fetchPracticeList from 'pages/Dashboard/services/addpatient.services';
import { fetchPracticeProvidersList, fetchProvidersAssociationsList } from 'pages/Dashboard/services/provider.services';
import { PracticeProvidersListResponse, ProviderDesgination } from 'pages/Dashboard/types/providers.types';
import { ToastProps, ToastType } from 'components/Common/Toast';
import exampleFile from 'assets/mock-patient-bulk-upload.png';
import { BulkUploadInviteFormConsent, SMSConsent } from 'pages/Dashboard/components/AddPatientConsents';
import Dropdown, { DropdownOption } from 'components/Common/Dropdown';
import { useFormContext } from 'react-hook-form';
import { logSentryError, trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import Theme from 'theme';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import useConfirm from 'hooks/use-confirm';
import CircularIndeterminate from 'components/Common/Spinner';


interface UploadPatientsDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (value: React.SetStateAction<boolean>) => void;
  setToastProps?: (props: ToastProps | null) => void;
}
// Acceptable file types for upload
const ACCEPTED_FILE_TYPES = '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf';

function UploadPatientsDrawer({ open, onClose, onSuccess,
  setToastProps }: UploadPatientsDrawerProps) {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [exampleOpen, setExampleOpen] = React.useState(false);
  const [providerError, setProviderError] = React.useState<string | null>(null);

  const { http, providerId, practiceId } = useHttp();
  const { user, getAccessTokenSilently } = useUnifiedAuth();
  const { control, watch, setValue, getValues } = useFormContext();

  const [practiceProviderOptions, setPracticeProviderOptions] = React
    .useState<DropdownOption[]>([]);

  const { showConfirm, ConfirmDialog } = useConfirm();

  useQuery('practices-list', {
    queryFn: fetchPracticeList(http.get, providerId),
    onSuccess: (data) => {
      if (data.practices?.length === 1) {
        setValue('bulkUploadPracticeId', data.practices[0].id);
      }
    },
  });

  const { isLoading: associationProvidersListLoading, data: associationProviderIds } = useQuery(
    [`${providers.providerAssociationList.queryUrl}`, providerId, 'upload-patients-form'],
    {
      queryFn: fetchProvidersAssociationsList(http.get),
      select: (data) => data.providers.map((provider) => provider.id),
    },
  );

  const selectedPracticeId = watch('bulkUploadPracticeId');
  const selectedProviderId = watch('bulkUploadProviderId');
  const { isFetching: providersLoading } = useQuery(
    [`${providers.practiceProvidersList.queryUrl}`, selectedPracticeId],
    {
      queryFn: fetchPracticeProvidersList(http.get, selectedPracticeId || ''),
      enabled: !associationProvidersListLoading && !!selectedPracticeId,
      onSuccess: (practiceProviders: PracticeProvidersListResponse) => {
        const newPracticeProviderOptions = practiceProviders.providers.reduce((acc, p) => {
          if (
            p.designation !== ProviderDesgination.PROVIDER_DESIGNATION_OFFICE_STAFF
            && (p.id === providerId || associationProviderIds?.includes(p.id))
          ) {
            acc.push({ label: `${p.firstName} ${p.lastName}`, value: p.id });
          }
          return acc;
        }, [] as DropdownOption[]);

        newPracticeProviderOptions.sort((a, b) => (a.label.split(' ')[1] || '').localeCompare(b.label.split(' ')[1] || ''));
        setPracticeProviderOptions(newPracticeProviderOptions);

        if (newPracticeProviderOptions.length === 1) {
          setValue('bulkUploadProviderId', newPracticeProviderOptions[0].value);
        }
      },
    },
  );

  React.useEffect(() => {
    if (open) {
      trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_DRAWER_OPENED);
    }
    else {
      // Clear selected file and form fields when drawer closes
      setSelectedFile(null);
      setValue('bulkUploadProviderId', '');
      setUploading(false);
      setProviderError(null);
    }
  }, [open, setValue]);

  React.useEffect(() => {
    if (selectedProviderId) {
      setProviderError(null);
    }
  }, [selectedProviderId]);

  const handleViewExampleFile = () => {
    setExampleOpen(true);
    trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_VIEW_EXAMPLE_FILE_CLICKED);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleCancelFile = () => {
    trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_CANCEL_FILE_SELECTED_CLICKED);
    setSelectedFile(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file: File) => {
    setSelectedFile(file);
    trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_FILE_SELECTED, {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });
  };

  const handleUpload = async () => {
    setProviderError(null);
    const finalProviderId = selectedProviderId || providerId;
    const finalPracticeId = selectedPracticeId || practiceId;
    if (!selectedProviderId) {
      setProviderError('Provider is required');
      return;
    }
    if (!selectedFile || !finalProviderId || !finalPracticeId || !user?.sub) return;
    setUploading(true);
    trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_FILE_SUBMIT_CLICKED, {
      fileName: selectedFile.name,
      fileType: selectedFile.type,
      fileSize: selectedFile.size,
    });
    try {
      const formData = new FormData();
      formData.append('provider_id', finalProviderId);
      formData.append('practice_id', finalPracticeId);
      formData.append('uploaded_by_user_id', providerId);
      formData.append('file', selectedFile);

      const apiUrl = bulkUploadUrls.patientBulkUpload.apiUrl(finalProviderId);
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        setSelectedFile(null);
        trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_SUCCESS, {
          fileName: selectedFile.name,
        });
        if (onSuccess) onSuccess(true);
        onClose();
      }
      else {
        let errorMsg = 'Failed to upload file. Please try again.';
        if (response.status === 413) {
          errorMsg = 'File size exceeds 4 MB. Please upload a smaller file.';
        }
        setToastProps?.({
          type: ToastType.error,
          message: errorMsg,
          open: true,
        });
        logSentryError(errorMsg);
        trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_FILE_UPLOAD_ERROR, {
          fileName: selectedFile.name,
        });
      }
    }
    catch (err) {
      let errorMessage = err instanceof Error ? err.message : 'Failed to upload file. Please try again.';
      if (
        typeof errorMessage === 'string'
        && errorMessage.toLowerCase().includes('received message larger than max')
      ) {
        errorMessage = 'File size exceeds 4 MB. Please upload a smaller file.';
      }
      logSentryError(`Error uploading file - ${errorMessage}`);
      setToastProps?.({
        type: ToastType.error,
        message: errorMessage,
        open: true,
      });
    }
    finally {
      setUploading(false);
    }
  };

  const handleDrawerClose = React.useCallback(() => {
    if (selectedFile && !uploading) {
      trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_DISCARD_POPUP_SHOWN, {
        hasUnsavedChanges: true,
      });
      showConfirm({
        message: 'Are you sure you want to navigate away?',
        onConfirm: () => {
          setSelectedFile(null);
          onClose();
        },
      });
    }
    else {
      onClose();
    }
  }, [selectedFile, uploading, showConfirm, onClose]);


  return (
    <>
      {uploading && <CircularIndeterminate />}
      <ConfirmDialog />
      <Drawer
        anchor='right'
        open={open}
        onClose={handleDrawerClose}
        PaperProps={{ sx: { width: 640, maxWidth: '100vw', p: 3 } }}
      >
        <Box display='flex' flexDirection='column' height='100%'>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={0.5}>
            <Typography variant='h6'>Upload multiple patients</Typography>
            <IconButton size='small' onClick={handleDrawerClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2.5 }} />
          <Box mb={2}>
            {practiceProviderOptions.length > 1 && (
              <Grid item>
                <Dropdown
                  control={control}
                  label='Select provider'
                  name='bulkUploadProviderId'
                  options={practiceProviderOptions}
                  required
                  disabled={!selectedPracticeId || providersLoading
                    || associationProvidersListLoading}
                  error={!!providerError}
                  errorColor={Theme.custom.colors.lightErrorMain}
                  onSelectOption={() => trackMixpanelEvent(MixpanelEventName
                    .BULK_UPLOAD_PATIENTS_PROVIDER_SELECTED, {
                    providerId: getValues('bulkUploadProviderId'),
                  })}
                />
                {providerError && (
                  <Typography
                    sx={{
                      color: Theme.custom.colors.lightErrorMain,
                      fontSize: 16,
                      mt: 0.5,
                      ml: 0.5,
                    }}
                  >
                    {providerError}
                  </Typography>
                )}
              </Grid>
            )}
          </Box>
          {!selectedFile && (
            <Box
              sx={{
                border: `2.5px dashed ${Theme.custom.colors.lightPrimaryMain}`,
                borderRadius: 2,
                width: '100%',
                minHeight: 220,
                background: Theme.custom.colors.white,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: Theme.custom.colors.primaryMain,
                fontSize: 22,
                mb: 2,
                mt: 1,
                textAlign: 'center',
                cursor: 'pointer',
                px: 2,
                py: 3,
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: Theme.custom.colors.primaryMain },
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <Typography
                variant='h5'
                sx={{
                  color: Theme.custom.colors.lightTextSecondary,
                  fontWeight: 500,
                  fontSize: 20,
                  mb: 1,
                  px: 2,
                  borderRadius: 1,
                  display: 'inline-block',
                }}
              >
                drag file here
              </Typography>
              <Typography variant='h6' sx={{ color: Theme.custom.colors.lightTextSecondary, fontWeight: 500, fontSize: 20, mb: 1 }}>
                - OR -
              </Typography>
              <Button
                variant='contained'
                component='label'
                sx={{
                  background: Theme.custom.colors.primaryMain,
                  color: Theme.custom.colors.white,
                  fontWeight: 500,
                  fontSize: 15,
                  textTransform: 'none',
                  boxShadow: '0px 1px 2px #00000014',
                  letterSpacing: 0.5,
                  borderRadius: 1,
                  '&:hover': { background: Theme.custom.colors.lightPrimaryMain },
                }}
                startIcon={<FolderOpenIcon />}
              >
                BROWSE FOR FILE
                <input
                  id='upload-patients-input'
                  type='file'
                  accept={ACCEPTED_FILE_TYPES}
                  hidden
                  onChange={handleFileInputChange}
                />
              </Button>
            </Box>
          )}
          {selectedFile && (
            <Box
              sx={{
                border: `2px solid ${Theme.custom.colors.primaryMain}`,
                borderRadius: 2,
                width: '100%',
                minHeight: 220,
                background: Theme.custom.colors.white,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: Theme.custom.colors.primaryMain,
                fontSize: 22,
                mb: 2,
                mt: 1,
                textAlign: 'center',
                px: 2,
                py: 3,
              }}
            >
              <FeedIcon sx={{ fontSize: 48, mb: 2, color: Theme.custom.colors.primaryMain }} />
              <Typography
                variant='h5'
                sx={{
                  color: Theme.custom.colors.lightTextSecondary,
                  fontWeight: 700,
                  fontSize: 16,
                  mb: 1,
                }}
              >
                &quot;
                {selectedFile.name}
                &quot; is ready
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: Theme.custom.colors.lightTextSecondary,
                  fontWeight: 400,
                  mb: 2,
                  fontSize: 16,
                }}
              >
                Click &quot;SUBMIT&quot; to upload
              </Typography>
              <Box display='flex' justifyContent='center' width='100%' gap={2}>
                <Button
                  variant='outlined'
                  sx={{
                    color: Theme.custom.colors.primaryMain,
                    borderColor: Theme.custom.colors.primaryMain,
                    fontWeight: 500,
                    fontSize: 15,
                    borderRadius: 1,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: Theme.custom.colors.lightPrimaryMain,
                      background: Theme.custom.colors.backgroundColorSecondary,
                    },
                  }}
                  onClick={handleCancelFile}
                >
                  CANCEL
                </Button>
                <Button
                  variant='contained'
                  sx={{
                    background: Theme.custom.colors.primaryMain,
                    color: Theme.custom.colors.white,
                    fontWeight: 500,
                    fontSize: 15,
                    borderRadius: 1,
                    textTransform: 'none',
                    boxShadow: '0px 1px 2px #00000014',
                    letterSpacing: 0.5,
                    '&:hover': { background: Theme.custom.colors.lightPrimaryMain },
                  }}
                  startIcon={<UploadFileIcon />}
                  onClick={handleUpload}
                  disabled={uploading || !selectedFile}
                >
                  SUBMIT
                </Button>
              </Box>
            </Box>
          )}
          <Box mt={3} mb={2} display='flex' flexDirection='row' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center'>
              <InsertDriveFileIcon
                sx={{
                  color: Theme.custom.colors.primaryMain,
                  fontSize: 24,
                  mr: 1,
                }}
              />
              <Link
                underline='hover'
                color='primary'
                sx={{ cursor: 'pointer', fontWeight: 400, fontSize: 16 }}
                onClick={handleViewExampleFile}
              >
                example file
              </Link>
            </Box>
          </Box>
          <Dialog open={exampleOpen} onClose={() => setExampleOpen(false)} maxWidth='md' fullWidth>
            <DialogContent sx={{ p: 0 }}>
              <img
                src={exampleFile}
                alt='Example patient bulk upload file'
                style={{ width: '100%', height: 'auto', display: 'block', background: Theme.custom.colors.white }}
              />
            </DialogContent>
          </Dialog>
          <Accordion
            sx={{
              boxShadow: 'none',
              borderRadius: 1,
              '&:before':
                { display: 'none' },
              transition: 'none',
            }}
            onChange={(_, expanded) => {
              trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_INSTRUCTIONS_CLICKED, {
                expanded,
              });
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderBottom: `1px solid ${Theme.custom.colors.divider}`,
                background: 'rgba(2, 136, 209, 0.04)',
                minHeight: 56,
                maxHeight: 56,
                '&.Mui-expanded': {
                  minHeight: 56,
                  maxHeight: 56,
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  color: Theme.custom.colors.primaryMain,
                  fontSize: 16,
                }}
              >
                Instructions
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant='body1' sx={{ fontWeight: 700, mb: 0.5 }}>
                  1. Get your patient list
                </Typography>
                <Typography variant='body1' sx={{ color: Theme.custom.colors.lightTextSecondary, mb: 1, ml: 2.5 }}>
                  Go to your EHR, EMR, or wherever you usually view your patient records.
                  <br />
                  Look for the section where you normally search for or view all patients.
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 700, mb: 0.5 }}>
                  2. Export the list
                </Typography>
                <Typography variant='body1' sx={{ color: Theme.custom.colors.lightTextSecondary, mb: 1, ml: 2.5 }}>
                  Find an Export or Download button on the page.
                  Most systems will let you export the list in either CSV, XLSX, or PDF format.
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 700, mb: 0.5 }}>
                  3. Confirm you have these 3 details
                </Typography>
                <Typography variant='body1' sx={{ color: Theme.custom.colors.lightTextSecondary, ml: 2.5 }}>
                  We only need a few fields to invite each patient:
                </Typography>
                <Box sx={{ ml: 2.5, mb: 1 }}>
                  <ol type='a' style={{ paddingLeft: 20, margin: 0, color: Theme.custom.colors.lightTextSecondary, fontSize: 16 }}>
                    <li>First name</li>
                    <li>Last name</li>
                    <li>Mobile phone number</li>
                  </ol>
                </Box>
                <Typography variant='body1' sx={{ fontWeight: 700, mb: 0.5 }}>
                  4. Upload your file
                </Typography>
                <Typography variant='body1' sx={{ color: Theme.custom.colors.lightTextSecondary, ml: 2.5 }}>
                  Drag and drop the file into the box below, or click to browse and upload it
                  manually. We accept CSV, Excel, and PDF files with the required columns.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Divider sx={{ mb: 2 }} />
          <Box mb={3}>
            <Box mb={2} mt={4} display='flex'>
              <SMSConsent />
            </Box>
            <Box mb={2}>
              <BulkUploadInviteFormConsent />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

export default UploadPatientsDrawer;
